<template>
  <template v-if="context">
    <PuppyFormItem v-if="isFieldVisible('faceMode')">
      <template #icon>
        <span class="field-label-text">模式</span>
      </template>
      <n-select
        size="small"
        :value="faceFilletMode"
        :options="faceFilletModeOptions"
        :disabled="!context.setFilletFaceFilletMode"
        @update:value="handleFaceFilletModeChange"
      />
    </PuppyFormItem>

    <PuppyFormItem v-if="showRadiusField">
      <template #icon>
        <span class="field-label-text">{{ faceFilletMode === 'asymmetric' ? '半径 1' : '半径' }}</span>
      </template>
      <div class="field-with-unit">
        <n-input-number
          size="small"
          show-button
          :value="radiusValue"
          :disabled="!context.setFilletRadius"
          :min="FACE_FILLET_RADIUS_MIN"
          :precision="3"
          :step="1"
          @update:value="handleRadiusCommit"
        />
        <span class="field-unit">mm</span>
      </div>
    </PuppyFormItem>

    <PuppyFormItem v-if="showRadius2Field">
      <template #icon>
        <span class="field-label-text">半径 2</span>
      </template>
      <div class="field-with-unit">
        <n-input-number
          size="small"
          show-button
          :value="radius2Value"
          :disabled="!context.setFilletRadius2"
          :min="FACE_FILLET_RADIUS_MIN"
          :precision="3"
          :step="1"
          @update:value="handleRadius2Commit"
        />
        <span class="field-unit">mm</span>
      </div>
    </PuppyFormItem>

    <PuppyFormItem v-if="showChordWidthField">
      <template #icon>
        <span class="field-label-text">弦宽度</span>
      </template>
      <div class="field-with-unit">
        <n-input-number
          size="small"
          show-button
          :value="chordWidthValue"
          :disabled="!context.setFilletChordWidth"
          :min="FACE_FILLET_RADIUS_MIN"
          :precision="3"
          :step="1"
          @update:value="handleChordWidthCommit"
        />
        <span class="field-unit">mm</span>
      </div>
    </PuppyFormItem>

    <PuppyFormItem v-if="showFlipField">
      <template #icon>
        <span class="field-label-text">翻转</span>
      </template>
      <n-checkbox
        :checked="faceFilletRadiiFlipped"
        :disabled="!context.setFilletFaceFilletRadiiFlipped"
        @update:checked="(value) => context.setFilletFaceFilletRadiiFlipped?.(value)"
      >
        翻转半径方向
      </n-checkbox>
    </PuppyFormItem>
  </template>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  NCheckbox,
  NInputNumber,
  NSelect,
} from 'naive-ui';
import type {
  FaceFilletMode,
} from '@puppycad/cad-core';
import {
  convertMToMm,
  convertMmToM,
} from '@puppycad/cad-core';
import type { PropertyManagerContext } from '@/core';
import type {
  BlendFieldAvailability,
  FilletPropertyPanelSpec,
} from '@/core/features/blend/panel-spec';
import { resolveFilletPropertyPanelSpec } from '@/core/features/blend/panel-spec';
import PuppyFormItem from '@/components/PuppyFormItem/PuppyFormItem.vue';

type FaceFilletPanelField = keyof FilletPropertyPanelSpec;

const props = defineProps<{
  context: PropertyManagerContext;
}>();

const supportSpec = computed(() => resolveFilletPropertyPanelSpec('face'));
const FACE_FILLET_RADIUS_MIN = 0.001;
const FACE_FILLET_RADIUS_MIN_M = convertMmToM(FACE_FILLET_RADIUS_MIN);
const faceFilletMode = computed<FaceFilletMode>(() => props.context.filletDefinition?.faceFilletMode ?? 'symmetric');
const radiusValue = computed(() => formatLengthForPropertyManager(props.context.filletRadius ?? props.context.filletDefinition?.radius ?? convertMmToM(10)));
const radius2Value = computed(() => formatLengthForPropertyManager(props.context.filletDefinition?.radius2 ?? convertMmToM(radiusValue.value)));
const chordWidthValue = computed(() => formatLengthForPropertyManager(props.context.filletDefinition?.chordWidth ?? convertMmToM(radiusValue.value)));
const faceFilletRadiiFlipped = computed(() => props.context.filletDefinition?.faceFilletRadiiFlipped === true);
const isChordWidthMode = computed(() => faceFilletMode.value === 'chord-width');
const isAsymmetricMode = computed(() => faceFilletMode.value === 'asymmetric');
const showRadiusField = computed(() => faceFilletMode.value === 'symmetric' || faceFilletMode.value === 'asymmetric');
const showRadius2Field = computed(() => isAsymmetricMode.value);
const showChordWidthField = computed(() => isChordWidthMode.value);
const showFlipField = computed(() => isAsymmetricMode.value);
const faceFilletModeOptions = computed(() => [
  { label: '对称', value: 'symmetric', disabled: !props.context.setFilletFaceFilletMode },
  { label: '非对称', value: 'asymmetric', disabled: !props.context.setFilletFaceFilletMode },
  { label: '弦宽度', value: 'chord-width', disabled: !props.context.setFilletFaceFilletMode },
]);

/**
 * 读取字段可用性。
 *
 * @param field 字段标识。
 * @returns 字段可用性。
 */
function resolveFieldAvailability(field: FaceFilletPanelField): BlendFieldAvailability {
  return supportSpec.value[field];
}

/**
 * 判断字段是否可见。
 *
 * @param field 字段标识。
 * @returns 是否可见。
 */
function isFieldVisible(field: FaceFilletPanelField): boolean {
  return resolveFieldAvailability(field) !== 'hidden';
}

//------ 面圆角属性长度单位转换 开始------
/**
 * 将底层米制长度转换为 PropertyManager 的毫米显示值。
 *
 * @param value 底层米制长度。
 * @returns 毫米显示值。
 */
function formatLengthForPropertyManager(value: number): number {
  // 面板展示毫米，底层仍使用 Three.js 米制单位。
  return Number(convertMToMm(value).toFixed(3));
}

/**
 * 将 PropertyManager 的毫米输入转换为底层米制长度。
 *
 * @param value 用户输入的毫米值。
 * @returns 米制长度。
 */
function normalizeLengthFromPropertyManager(value: number | null): number {
  const normalizedMm = Math.max(FACE_FILLET_RADIUS_MIN, value ?? FACE_FILLET_RADIUS_MIN);
  return Math.max(FACE_FILLET_RADIUS_MIN_M, convertMmToM(normalizedMm));
}
//------ 面圆角属性长度单位转换 结束------

/**
 * 提交主半径。
 *
 * @param value 输入值。
 */
function handleRadiusCommit(value: number | null): void {
  props.context.setFilletRadius?.(normalizeLengthFromPropertyManager(value), null);
}

/**
 * 提交次半径。
 *
 * @param value 输入值。
 */
function handleRadius2Commit(value: number | null): void {
  props.context.setFilletRadius2?.(normalizeLengthFromPropertyManager(value), null);
}

/**
 * 提交面圆角弦宽度。
 *
 * @param value 输入值。
 */
function handleChordWidthCommit(value: number | null): void {
  props.context.setFilletChordWidth?.(normalizeLengthFromPropertyManager(value), null);
}

/**
 * 切换面圆角模式。
 *
 * @param value 目标模式。
 */
function handleFaceFilletModeChange(value: string | null): void {
  if (
    value === 'symmetric'
    || value === 'chord-width'
    || value === 'asymmetric'
  ) {
    props.context.setFilletFaceFilletMode?.(value);
  }
}
</script>

<style scoped src="./BlendPropertySection.css"></style>
